import { useCallback, useState } from 'react';
import { useSelectEvent } from '@stitch-fix/event-reporter';
import { Button, Grid } from '@stitch-fix/mode-react';
import { OutfitsModal } from '../OutfitsModal/OutfitsModal';
import { CancelReturnModal } from '../CancelReturnModal/CancelReturnModal';
type BoughtItemsActionFooterProps = {
  mayHaveOutfits: boolean;
  returnInProgress: boolean;
  skuId: string;
  orderId: string;
};

/**
 * Determines what buttons to display in the Product Card's action footer
 */
const actionFooter = (mayHaveOutfits: boolean, returnInProgress: boolean, onOutfitsClick: (clicked: boolean) => void, onCancelReturnClick: (clicked: boolean) => void) => {
  const outfitsBtn = viewOutfitsButton(onOutfitsClick);
  const cancelReturnBtn = cancelReturnButton(onCancelReturnClick);
  if (returnInProgress && mayHaveOutfits) {
    return <Grid columns={{
      sm: 2
    }}>{[outfitsBtn, cancelReturnBtn]}</Grid>;
  }
  if (mayHaveOutfits && !returnInProgress) {
    return [outfitsBtn];
  }
  if (!mayHaveOutfits && returnInProgress) {
    return [cancelReturnBtn];
  }
  return [];
};

/**
 * Renders View Outfits button under the Product Card
 * Clicking this button opens the outfit modal
 */

const viewOutfitsButton = (onButtonClick: (clicked: boolean) => void) => <Button onClick={() => onButtonClick(true)} variant="outline" width="fill" key="view-outfits-button">
    View Outfits
  </Button>;

/**
 * Renders Cancel Return button under the Product Card
 * Clicking this button opens the Cancel Return modal
 */

const cancelReturnButton = (onButtonClick: (clicked: boolean) => void) => <Button onClick={() => onButtonClick(true)} variant="filled-staple" width="fit" key="cancel-return-button">
    Cancel Return
  </Button>;

/**
 * Renders the Bought Item Product Card action footer with the View Outfits button and
 * Cancel Return button. This handles opening / closing the Outfits modal and
 * handles the returnInProgressCancel mutation from the Cancel Return modal
 */
export const BoughtItemsActionFooter = ({
  mayHaveOutfits,
  returnInProgress,
  skuId,
  orderId
}: BoughtItemsActionFooterProps) => {
  const [outfitsModalOpened, setOutfitsModalOpened] = useState<boolean>(false);
  const [cancelReturnModalOpened, setCancelReturnModalOpened] = useState<boolean>(false);
  const handleOutfitsModalClose = () => {
    setOutfitsModalOpened(false);
  };
  const handleCancelReturnModalClose = () => {
    setCancelReturnModalOpened(false);
  };
  const {
    reportSelectEvent
  } = useSelectEvent();
  const handleOutfitsModalOpen = useCallback(() => {
    reportSelectEvent({
      schema: 'sku_select',
      name: 'view_outfits',
      action_name: 'open_outfits_modal',
      sku_id: skuId
    });
    setOutfitsModalOpened(true);
  }, [reportSelectEvent, skuId]);
  return <>
      {actionFooter(mayHaveOutfits, returnInProgress, handleOutfitsModalOpen, setCancelReturnModalOpened)}
      <OutfitsModal skuId={skuId} open={outfitsModalOpened} handleModalClose={handleOutfitsModalClose} />
      <CancelReturnModal skuId={skuId} orderId={orderId} open={cancelReturnModalOpened} handleModalClose={handleCancelReturnModalClose} />
    </>;
};